<template>
  <form @submit.prevent="submit" class="tito-ticket-show">
    <tito-ticket-header
      :ticket="ticket"
      :registration="registration"
      :locale="locale"
      :index="index"
    />
    <section class="tito-ticket-show--content">
      <header class="tito-ticket-detail--basic-info">
        <p class="tito-error" v-if="baseErrors">
          {{ baseErrors }}
        </p>

        <h2 class="tito-ticket-detail--basic-info-heading">
          {{ $t("views.tickets._form.basic_info.heading", locale) }}
        </h2>
      </header>
      <div class="tito-fields-required">
        {{ $t("fields_required", locale) }}
      </div>
      <div class="tito-ticket-details--first-and-last">
        <div class="tito-ticket-details-first-name">
          <label
            for="tito-ticket-first-name"
            class="tito-ticket-first-name-label"
          >
            <font-awesome-icon :icon="['fas', 'user']" />
            {{ $t("views.tickets._form.attendee_first_name_label", locale) }}
            <span v-if="rules.name.required" class="tito-required"> *</span>
          </label>
          <input
            v-focus
            ref="tito-ticket-first-name"
            name="tito-ticket-first-name"
            type="text"
            id="tito-ticket-first-name"
            placeholder="Jane"
            class="tito-ticket-first-name-field"
            v-model="details.first_name"
            v-validation
          />
          <span class="tito-error" v-if="errors.first_name">
            {{ errors.first_name.join(", ") }}
          </span>
        </div>

        <div class="tito-ticket-details-last-name">
          <label
            for="tito-ticket-last-name"
            class="tito-ticket-last-name-label"
          >
            <font-awesome-icon :icon="['fas', 'user']" />
            {{ $t("views.tickets._form.attendee_last_name_label", locale) }}
            <span v-if="rules.name.required" class="tito-required"> *</span>
          </label>
          <input
            type="text"
            name="tito-ticket-last-name"
            id="tito-ticket-last-name"
            placeholder="Smyth"
            class="tito-ticket-last-name-field"
            v-model="details.last_name"
            v-validation
          />
          <span class="tito-error" v-if="errors.last_name">
            {{ errors.last_name.join(", ") }}
          </span>
        </div>
      </div>
      <div class="tito-ticket-details-email">
        <label for="tito-ticket-email" class="tito-ticket-email-label">
          <font-awesome-icon :icon="['far', 'envelope']" />
          {{ $t("views.tickets._form.attendee_email_label", locale) }}
          <span v-if="rules.email.required" class="tito-required"> *</span>
        </label>
        <input
          :class="{
            'tito-field--disabled': assigned,
          }"
          type="email"
          name="tito-ticket-email"
          id="tito-ticket-email"
          placeholder="you@example.com"
          class="tito-field tito-ticket-email-field"
          v-model="details.email"
          v-validation="'email'"
          :disabled="assigned"
          :required="rules.email.required"
          @blur="authorizeEmail($event.target, ticket, details.email)"
          @input="resetEmailAuthorized($event.target)"
        />
        <p class="tito-error" v-if="emailErrors">
          {{ emailErrors }}
        </p>
        <button
          class="tito-ticket-details--reassign-button"
          v-if="canReassign"
          @click="reassign"
          type="button"
        >
          {{ $t("views.tickets._status.reassign_ticket", locale) }}
        </button>
      </div>

      <email-not-authorized-warning
        :contact-email="event.contact_email"
        :locale="locale"
        v-if="emailAuthorized === false"
      />

      <div
        class="tito-ticket-details-company-name"
        v-if="rules.company_name.shown"
      >
        <label
          for="tito-ticket-company-name"
          class="tito-field-label tito-ticket-company-name-label"
        >
          <font-awesome-icon :icon="['far', 'building']" />
          {{ $t("views.tickets._form.attendee_company_name", locale) }}
          <span v-if="rules.company_name.required" class="tito-required">
            *</span
          >
        </label>
        <input
          name="tito-ticket-company-name"
          id="tito-ticket-company-name"
          placeholder="Acme Inc."
          class="tito-field tito-ticket-company-name-field"
          v-model="details.company_name"
          :required="rules.company_name.required"
        />
        <span class="tito-error" v-if="errors.company_name">
          {{ errors.company_name.join(", ") }}
        </span>
      </div>

      <div class="tito-ticket-details-job-title" v-if="rules.job_title.shown">
        <label
          for="tito-ticket-job-title"
          class="tito-field-label tito-ticket-job-title-label"
        >
          <font-awesome-icon :icon="['far', 'building']" />
          {{ $t("views.tickets._form.attendee_job_title_label", locale) }}
          <span v-if="rules.job_title.required" class="tito-required"> *</span>
        </label>
        <input
          name="tito-ticket-job-title"
          id="tito-ticket-job-title"
          :placeholder="
            $t('views.tickets._form.attendee_job_title_placeholder', locale)
          "
          class="tito-field tito-ticket-job-title-field"
          v-model="details.job_title"
          :required="rules.job_title.required"
        />
        <span class="tito-error" v-if="errors.job_title">
          {{ errors.job_title.join(", ") }}
        </span>
      </div>
      <div
        class="tito-ticket-details-phone-number"
        v-if="rules.phone_number.shown"
      >
        <label
          for="tito-ticket-phone-number"
          class="tito-field-label tito-ticket-phone-number-label"
        >
          <font-awesome-icon :icon="['fas', 'phone']" />
          {{ $t("views.tickets._form.attendee_phone_number_label", locale) }}
          <span v-if="rules.phone_number.required" class="tito-required">
            *</span
          >
        </label>
        <input
          name="tito-ticket-phone-number"
          id="tito-ticket-phone-number"
          placeholder="555-555-5555"
          class="tito-field tito-ticket-phone-number-field"
          v-model="details.phone_number"
          :required="rules.phone_number.required"
        />
        <span class="tito-error" v-if="errors.phone_number">
          {{ errors.phone_number.join(", ") }}
        </span>
      </div>

      <template v-if="canConsent">
        <div class="tito-ticket-detail--data-privacy">
          <header>
            <h2>
              {{ $t("views.tickets._form.data_privacy.heading", locale) }}
            </h2>
            <p
              class="tito-ticket-details--consented-at"
              v-if="ticket.consented"
            >
              {{ consentedAt }}
            </p>
          </header>
          <template v-if="ticket.consented">
            <div class="tito-ticket-details--consent-statement">
              {{ consentStatement }}
            </div>
          </template>
          <p
            class="tito-text-warning"
            v-else-if="showNoAttendeeConsentWarning"
            v-html="noAttendeeConsentWarning"
          />
          <template v-else>
            <label class="tito-ticket-details--consent-statement--label">
              <input
                class="tito-ticket-details--consent-statement--checkbox"
                v-model="details.consent"
                required="true"
                type="checkbox"
                :true-value="ticket.consent_statement_version"
              />
              <span class="tito-required">*</span>
              <span v-html="consentStatement"></span>
            </label>
            <p
              class="tito-ticket-details--consent-statement--link"
              v-html="
                $t('views.tickets._form.data_privacy.organizer_terms', locale, {
                  url: ticket.smallprint_url,
                })
              "
            ></p>
          </template>
        </div>
      </template>

      <template v-if="ticket.opt_ins.length > 0 && showOptIns">
        <header class="tito-ticket-detail--opt-ins">
          <h2 class="tito-ticket-detail--opt-ins-heading">
            {{ $t("views.registrations._form.opt_ins.heading") }}
          </h2>
        </header>
        <div
          v-for="opt_in in ticket.opt_ins"
          class="tito-ticket-details-opt-in"
        >
          <label class="tito-ticket-details-opt-in--label">
            <input
              v-model="details.accepted_opt_in_ids"
              type="checkbox"
              :value="opt_in.id"
            />
            {{ translate(opt_in, "name") }}
          </label>
          <p
            class="tito-ticket-details-opt-in--description"
            v-if="opt_in.description"
            v-html="translate(opt_in, 'description')"
          ></p>
        </div>
      </template>

      <template v-if="allocatableActivityGroups.length > 0">
        <header class="tito-ticket-detail--additional-info">
          <h2 class="tito-ticket-detail--additional-info-heading">
            Ticket selections
          </h2>
          <postpone-answers-link
            :locale="locale"
            v-if="registration"
            @input="(value) => (details.postpone_answers = value)"
          />
        </header>

        <p
          class="tito-ticket-details--postpone-answers"
          v-if="details.postpone_answers"
        >
          {{
            $t("views.tickets._form.survey.will_send_required_question_email")
          }}
        </p>
        <tito-activity-group
          v-else
          v-for="activityGroup in allocatableActivityGroups"
          :key="`activity-group-${activityGroup.id}`"
          :activity-group="activityGroup"
          :locale="locale"
          v-model="chosenActivityIdsByActivityGroupId[activityGroup.id]"
        />
      </template>

      <template v-if="questions && questions.length > 0">
        <header class="tito-ticket-detail--additional-info">
          <h2 class="tito-ticket-detail--additional-info-heading">
            {{ $t("views.tickets._form.additional_info.heading", locale) }}
          </h2>
          <postpone-answers-link
            :locale="locale"
            v-if="registration && allocatableActivityGroups.length == 0"
            @input="(value) => (details.postpone_answers = value)"
          />
        </header>

        <p
          class="tito-ticket-details--postpone-answers"
          v-if="details.postpone_answers"
        >
          {{
            $t("views.tickets._form.survey.will_send_required_question_email")
          }}
        </p>

        <questions
          :questions="questions"
          :chosen-activity-ids="chosenActivityIds"
          :errors="errors"
          :locale="locale"
          :ticket="ticket"
          @input="answersChanged"
          v-else
        />
      </template>
    </section>

    <div class="tito-ticket-show--footer">
      <a href="#" class="tito-ticket-show--back" @click.prevent="back">{{
        $t("back")
      }}</a>
      <button
        :disabled="submitting"
        class="tito-ticket-show--submit"
        type="submit"
      >
        <template v-if="submitting">
          <font-awesome-icon :icon="['far', 'spinner']" spin />
        </template>
        <template v-else>
          {{ saveLabel }}
        </template>
      </button>
    </div>
  </form>
</template>

<script>
import TitoActivityGroup from "./ActivityGroup.vue";

import fecha from "fecha";
import focus from "js/src/directives/focus.js";
import validation from "js/src/directives/validation.js";
import EmailNotAuthorizedWarning from "../EmailNotAuthorizedWarning.vue";
import PostponeAnswersLink from "./PostponeAnswersLink.vue";
import Questions from "./Questions.vue";
import TicketMixins from "../../mixins/TicketMixins.js";
import TicketSetQuestions from "application/mixins/ticketSetQuestions.js";
import TitoTicketHeader from "./Header.vue";

import Tito from "js/src/client/client.js";
import emailAuthorization from "js/src/mixins/emailAuthorization.js";
import translate from "js/src/mixins/translate.js";
import uniq from "lodash/uniq";
import Vue from "vue";

export default {
  components: {
    EmailNotAuthorizedWarning,
    PostponeAnswersLink,
    Questions,
    TitoActivityGroup,
    TitoTicketHeader,
  },
  directives: { focus, validation },
  mixins: [emailAuthorization, TicketMixins, TicketSetQuestions, translate],

  props: ["index", "locale", "registration", "ticket", "who", "beans"],

  data() {
    let who = "";
    let ticket = this.ticket || {};
    let chosenActivityIdsByActivityGroupId = {};
    (this.ticket.activity_groups || []).forEach((activityGroup) => {
      chosenActivityIdsByActivityGroupId[activityGroup.id] =
        activityGroup.chosen_activity_ids;
    });
    let details = {
      first_name: ticket.first_name,
      last_name: ticket.last_name,
      email: ticket.email,
      phone_number: ticket.phone_number,
      postpone_answers: null,
      company_name: ticket.company_name,
      job_title: ticket.job_title,
      consent: null,
      who: this.who,
    };

    let showOptIns = this.getShowOptIns();
    if (showOptIns) {
      details.accepted_opt_in_ids = this.acceptedOptInIds();
    }
    return {
      details: details,
      answers: null,
      authorizingEmail: false,
      emailAuthorized: null,
      event: null,
      questions: null,
      chosenActivityIdsByActivityGroupId: chosenActivityIdsByActivityGroupId,
      errors: {},
      showOptIns: showOptIns,
      submitting: false,
      originalClosingDisabledState: this.$store.state.closingDisabled,
    };
  },

  created() {
    this.getEvent();
    this.populateBasedOnWho();
    this.setQuestions(this.chosenActivityIds);
  },

  mounted() {
    this.$store.commit("setClosingDisabled", true);
  },

  beforeDestroy() {
    this.resetOriginalClosingDisabledState();
  },

  computed: {
    allocatableActivityGroups() {
      return this.ticket.activity_groups.filter(
        (activityGroup) =>
          activityGroup.allocatable_public_optional_activities.length > 0,
      );
    },

    allocatablePublicOptionalActivities() {
      return uniq(
        this.ticket.activity_groups.flatMap(
          (activityGroup) =>
            activityGroup.allocatable_public_optional_activities,
        ),
      );
    },

    assigned() {
      return this.who === "me" || !!this.ticket.email;
    },

    baseErrors() {
      if (this.errors.base) {
        return this.errors.base.join(", ");
      }
    },

    chosenActivityIds() {
      return Object.values(this.chosenActivityIdsByActivityGroupId).flat();
    },

    emailErrors() {
      if (this.errors.email) {
        return this.errors.email.join(", ");
      }
    },

    canConsent() {
      return (
        this.ticket.consentable &&
        (!this.registration || this.registrantViewingOwnTicket)
      );
    },

    canReassign() {
      return (
        !!this.ticket.email &&
        this.ticket.state != "void" &&
        !this.ticket.changes_locked
      );
    },

    consentStatement() {
      return this.translate(this.ticket, "consent_statement");
    },

    consentedAt() {
      return this.$t("views.tickets._form.data_privacy.consented_at", {
        consented_at: fecha.format(
          new Date(this.ticket.consented_at),
          "D MMM YYYY",
        ),
      });
    },

    noAttendeeConsentWarning() {
      return this.$t(
        "views.tickets._form.data_privacy.no_attendee_consent_warning",
        this.locale,
      );
    },

    params() {
      return Object.assign(this.details, {
        answers: this.answers,
        chosen_activity_ids: this.chosenActivityIds,
      });
    },

    registrantViewingOwnTicket() {
      return (
        this.registration &&
        this.registration.email?.length > 0 &&
        this.registration.email == this.ticket.email
      );
    },

    saveLabel() {
      if (this.who === "dunno") {
        return this.$t("views.tickets._dunno.keep_unassigned", this.locale);
      }
      if (this.who === "somebody") {
        return this.$t(
          "views.tickets._form.survey.submit_label.send_ticket",
          this.locale,
        );
      }
      return this.$t(
        "views.tickets._form.survey.submit_label.save",
        this.locale,
      );
    },

    showNoAttendeeConsentWarning() {
      return (
        this.registration &&
        this.ticket.email &&
        this.ticket.email.length > 0 &&
        this.registration.email != this.ticket.email
      );
    },

    url() {
      return `/${this.$route.params.accountId}/${this.$route.params.eventId}/tickets/${this.$route.params.ticketId}`;
    },

    rules() {
      return this.ticket.rules;
    },
  },

  methods: {
    acceptedOptInIds() {
      return this.ticket.opt_ins
        .filter((opt_in) => opt_in.opted_in)
        .map((opt_in) => opt_in.id);
    },

    async getEvent() {
      this.event = this.$store.getters["event/find"](
        `${this.$route.params.accountId}/${this.$route.params.eventId}`,
      );
    },

    getShowOptIns() {
      let assigningToSelf = !this.ticket.assigned && this.who == "me";
      let assignedAndOwnTicket =
        this.ticket.assigned &&
        (!this.registration || this.ticket.email == this.registration.email);
      return assigningToSelf || assignedAndOwnTicket;
    },

    populateBasedOnWho() {
      if (this.who == "me") {
        this.details.first_name = this.registration.first_name;
        this.details.last_name = this.registration.last_name;
        this.details.email = this.registration.email;
        this.details.company_name = this.registration.company_name;
        this.details.job_title = this.registration.job_title;
        this.details.phone_number = this.registration.phone_number;
        this.details.accepted_opt_in_ids = this.registration.opt_ins
          .filter((opt_in) => opt_in.opted_in)
          .map((opt_in) => opt_in.id);
      } else if (this.who == "somebody") {
        this.details.first_name = null;
        this.details.last_name = null;
        this.details.email = null;
        this.details.company_name = null;
        this.details.phone_number = null;
      }
    },

    reassign() {
      if (this.registration) {
        this.$router.push({ name: "registrationTicketReassign" });
      } else {
        this.$router.push({ name: "ticketReassign" });
      }
    },

    resetOriginalClosingDisabledState() {
      this.$store.commit(
        "setClosingDisabled",
        this.originalClosingDisabledState,
      );
    },

    answersChanged(answers) {
      this.answers = answers;
      this.setQuestions(this.chosenActivityIds);
    },

    async submit() {
      if (this.submitting) return;
      try {
        this.submitting = true;
        await this.ticket.update(this.params);
        await this.reloadRegistrationOrTicket();
        this.$router.push(this.afterUpdateRoute());
      } catch (error) {
        this.setErrors(error);
        this.submitting = false;
      }
    },
  },

  watch: {
    chosenActivityIds(newChosenActivityIds) {
      this.setQuestions(newChosenActivityIds);
    },
  },
};
</script>
